/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
/*
 * The UX and glue to tanstack/react-table for a table of fingerprints.
 */
import { useEffect, useMemo, useRef, useState, RefObject } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BTable } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faFilter, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FingerprintThin } from '../data';
import { Badge } from 'react-bootstrap';
import { Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
    Updater,
  useReactTable,
} from '@tanstack/react-table'
import { SortingState } from '@tanstack/react-table';
import Form from 'react-bootstrap/Form';

const VIRTUAL_ROW_HEIGHT = 30;
const VIRTUAL_OVERSCAN = 8;

function FingerprintsTable({
    rowSelection,
    setRowSelection,
    data,
    scrollContainerRef,
    scrollToId,
    scrollTrigger,
    scrollBehavior,
    sorting,
    onSortingChange,
    onVisibleIdChange,
    onRowClick,
}: {
    rowSelection: Record<string, boolean>,
    setRowSelection: (value: Record<string, boolean>) => void,
    data: FingerprintThin[],
    scrollContainerRef?: RefObject<HTMLDivElement | null>,
    scrollToId?: number,
    scrollTrigger?: number,
    scrollBehavior?: ScrollBehavior,
    sorting: SortingState,
    onSortingChange: (sorting: Updater<SortingState>) => void,
    onVisibleIdChange?: (id: number) => void,
    onRowClick: (id: number) => void,
}) {
    const [ columnVisibility, setColumnVisibility ] = useState<Record<string, boolean>>({
        'select-col': true,
        'pvkey': false,
        'layer': true,
        'backend_selected': true,
        'tag_count': true,
        'fid': true,
        'country': true,
        'product': true,
        'product_tag': false,
        'vendor': true,
        'vendorLocal': true,
        'vendor_tag': false,
        'title': true,
        'description': true,
        'presentation': false,
    });
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [viewportHeight, setViewportHeight] = useState<number>(700);
    const programmaticScrollRef = useRef<boolean>(false);
    const programmaticScrollTimerRef = useRef<number | undefined>(undefined);
    const lastEmittedVisibleIdRef = useRef<number | undefined>(undefined);
    const hasUserScrollRef = useRef<boolean>(false);

    const columns: ColumnDef<FingerprintThin>[] = useMemo(() => [
            {
                id: 'select-col',
                size: 24,
                header: ({ table }) => (
                <Form.Check
                    type="checkbox"
                    checked={table.getIsAllRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
                ),
                cell: ({ row }) => (
                    <Form.Check
                        type="checkbox"
                        checked={row.getIsSelected()}
                        disabled={!row.getCanSelect()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                ),
            },
            {
                id: 'fid',
                header: 'Id',
                size: 38,
                accessorKey: 'fid',
                filterFn: 'equals',
            },
            {
                header: "Layer",
                accessorKey: 'layer',
                size: 32,
                id: 'layer',
                filterFn: 'equals',
                cell: ({ row }) => {
                    const value = row.getValue('layer');
                    let bg;
                    switch (value) {
                        case 0: bg = 'text-center bg-primary text-white'; break;
                        case 1: bg = 'text-center bg-light text-dark'; break;
                        case 10: bg = 'text-center bg-success text-white'; break;
                        case 6: bg = 'text-center bg-danger text-white'; break;
                        case 9: bg = 'text-center bg-warning text-dark'; break;
                        default: bg = 'text-center bg-secondary text-white'; break;
                    }
                    return (
                        <div className={bg}>{value as string}</div>
                    );
                }
            },
            {
                id: 'backend_selected',
                header: 'WS',
                size: 51,
                accessorKey: 'backend_selected',
                cell: ({ row }) => {
                    let selected = row.getValue('backend_selected');
                    return selected ? (
                        <Badge pill bg="primary">Wrk</Badge>
                    ) : null;
                },
            },
            {
                header: "Tag",
                size: 38,
                accessorKey: 'tag_count',
                id: 'tag_count',
                cell: ({ row }) => {
                    const value = Number(row.getValue('tag_count') || 0);
                    switch (value) {
                    case 0: return null;
                    case 1: return (
                        <Badge pill bg="primary">{value}</Badge>
                    );
                    case 2: return (
                        <Badge pill bg="success">{value}</Badge>
                    );
                    case 3: return (
                        <Badge pill bg="info">{value}</Badge>
                    );
                    case 4: return (
                        <Badge pill bg="warning">{value}</Badge>
                    );
                    default: return (
                        <Badge pill bg="info">{value as any}</Badge>
                    );
                    }
                },
            },
            {
                header: 'PVKey',
                id: 'pvkey',
                accessorKey: 'pvkey',
                cell: (props) => {
                    const row = props.row;
                    return (
                        <span
                            style={{ cursor: 'pointer' }}
                            className="text-primary"
                            >
                            {row.getValue('pvkey') as string}
                        </span>
                    );
                }
            },
            {
                header: 'CC',
                id: 'country',
                size: 25,
                accessorKey: 'country_code',
                filterFn: 'includesString',
                cell: ({ row }) => {
                    const value = String(row.getValue('country_code') || '');
                    return ( 
                        <span>{value}</span>
                    );
                },
            },
            {
                header: 'Vendor',
                id: 'vendor',
                size: 170,
                accessorKey: 'vendor',
                filterFn: 'includesString',
                cell: ({ row }) => {
                    let vendorLocal = '';
                    const localVendor = String(row.original.vendor_local || '');
                    if (localVendor) {
                        vendorLocal = ` (${localVendor})`;
                    }
                    return ( 
                        <span>{row.getValue('vendor')}{vendorLocal}</span>
                    );
                },
            },
            {
                header: 'Product',
                id: 'product',
                size: 170,
                accessorKey: 'product',
                filterFn: 'includesString',
            },
            {
                header: "VendorTag",
                accessorKey: 'vendor_tag',
                size: 200,
                id: 'vendor_tag',
            },
            {
                header: "ProductTag",
                accessorKey: 'product_tag',
                size: 200,
                id: 'product_tag',
            },
            {
                header: "Title",
                accessorKey: 'title',
                id: 'title',
                size: 300,
            },
            {
                header: "Description",
                accessorKey: 'description',
                id: 'description',
                size: 400,
                cell: ({ row }) => {
                    const value = row.getValue('description');
                    return (
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden',
                            textOverflow: 'ellipsis' }}>
                            {value as string}
                        </span>
                    );
                }
            },
            {
                header: "Presentation",
                accessorKey: 'presentation',
                id: 'presentation',
            }
    ], []);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableGlobalFilter: false,
        enableMultiRowSelection: true,
        onRowSelectionChange: setRowSelection as any,
        getRowId: (row : any) => row['fid'],
        state: {
            rowSelection,
            columnVisibility,
            sorting,
        },
        onColumnVisibilityChange: setColumnVisibility,
        onSortingChange: onSortingChange as any,
    })

    const rows = table.getRowModel().rows;
    const totalRows = rows.length;
    const firstVisibleRow = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN);
    const rowsInView = Math.ceil(viewportHeight / VIRTUAL_ROW_HEIGHT) + (VIRTUAL_OVERSCAN * 2);
    const lastVisibleRow = Math.min(totalRows, firstVisibleRow + rowsInView);
    const visibleRows = rows.slice(firstVisibleRow, lastVisibleRow);
    const topSpacerHeight = firstVisibleRow * VIRTUAL_ROW_HEIGHT;
    const bottomSpacerHeight = Math.max(0, (totalRows - lastVisibleRow) * VIRTUAL_ROW_HEIGHT);

    useEffect(() => {
        const node = scrollContainerRef?.current;
        if (!node) {
            return;
        }

        const updateViewport = () => {
            setViewportHeight(node.clientHeight || 700);
        };

        updateViewport();
        window.addEventListener('resize', updateViewport);
        return () => {
            window.removeEventListener('resize', updateViewport);
        };
    }, [scrollContainerRef]);

    useEffect(() => {
        if (!scrollContainerRef?.current || !scrollToId) {
            return;
        }

        const targetIndex = rows.findIndex((row) => Number(row.id) === scrollToId || row.original.fid === scrollToId);
        if (targetIndex < 0) {
            return;
        }

        const centeredTop = Math.max(0, (targetIndex * VIRTUAL_ROW_HEIGHT) - Math.floor(viewportHeight / 2));
        programmaticScrollRef.current = true;
        if (programmaticScrollTimerRef.current) {
            window.clearTimeout(programmaticScrollTimerRef.current);
        }
        scrollContainerRef.current.scrollTo({ top: centeredTop, behavior: scrollBehavior || 'smooth' });
        setScrollTop(centeredTop);
        programmaticScrollTimerRef.current = window.setTimeout(() => {
            programmaticScrollRef.current = false;
        }, 300);
    }, [scrollContainerRef, scrollToId, scrollTrigger, viewportHeight, scrollBehavior]);

    useEffect(() => {
        if (!onVisibleIdChange || totalRows <= 0) {
            return;
        }
        if (!hasUserScrollRef.current) {
            return;
        }
        if (programmaticScrollRef.current) {
            return;
        }
        const centerIndex = Math.max(
            0,
            Math.min(totalRows - 1, Math.floor((scrollTop + (viewportHeight / 2)) / VIRTUAL_ROW_HEIGHT))
        );
        const centerRow = rows[centerIndex];
        if (!centerRow) {
            return;
        }
        const centerFid = Number(centerRow.original.fid);
        if (!centerFid) {
            return;
        }
        if (lastEmittedVisibleIdRef.current === centerFid) {
            return;
        }
        lastEmittedVisibleIdRef.current = centerFid;
        onVisibleIdChange(centerFid);
    }, [scrollTop, viewportHeight, rows, totalRows, onVisibleIdChange]);

    useEffect(() => {
        return () => {
            if (programmaticScrollTimerRef.current) {
                window.clearTimeout(programmaticScrollTimerRef.current);
            }
        };
    }, []);

    return (
        <Container fluid>
            <Row ref={scrollContainerRef} style={{ height: '80vh',
                                    display: 'block',
                                    overflowY: 'auto',
                                    overflowX: 'hidden' }}
                onScroll={(e) => {
                    if (!programmaticScrollRef.current) {
                        hasUserScrollRef.current = true;
                    }
                    setScrollTop((e.currentTarget as HTMLDivElement).scrollTop);
                }}>
                <BTable hover size="sm" variant="dark" style={{ tableLayout: 'fixed' }}>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                            <th style={{ position: 'sticky',
                                        top: 0,
                                        background: '#fff',
                                        zIndex: 1,
                                        width: `${header.getSize()}px` }}
                                key={header.id} colSpan={header.colSpan}
                            >
                                <div onClick={header.column.getToggleSortingHandler()}>
                                    {header.isPlaceholder ? null : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    ) }
                                    {header.column.getIsSorted() ? (
                                        <SortIcon>
                                            <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                        </SortIcon>
                                    ) : null }
                                </div>
                            </th>
                        ))}</tr>
                    ))}</thead>

                    <tbody>
                        {topSpacerHeight > 0 ? (
                            <tr>
                                <td colSpan={table.getVisibleLeafColumns().length} style={{ height: `${topSpacerHeight}px`, padding: 0, border: 0 }} />
                            </tr>
                        ) : null}
                        {visibleRows.map(row => (
                        <tr key={row.id}
                            data-id={row.original.fid}
                            className={row.getIsSelected() ? 'table-info' : undefined}
                        >
                            {row.getVisibleCells().map(cell => cell.column.id === 'select-col' ? (
                                <td onClick={row.getToggleSelectedHandler()}
                                    key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ) : (
                                <td onClick={() => onRowClick(Number(row.getValue('fid')))}
                                    style={{ width: `${cell.column.getSize()}px` }}
                                    key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                        ))}
                        </tr>
                    ))}
                        {bottomSpacerHeight > 0 ? (
                            <tr>
                                <td colSpan={table.getVisibleLeafColumns().length} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: 0 }} />
                            </tr>
                        ) : null}
                    </tbody>
                    <tfoot>{table.getFooterGroups().map(footerGroup => (
                        <tr key={footerGroup.id}>{footerGroup.headers.map(header => (
                            <th key={header.id} colSpan={header.colSpan}>{header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext()
                            )}</th>
                        ))}</tr>
                    ))}</tfoot>
                </BTable>
            </Row>
    </Container>
  )
}

//Styles
const SortIcon = styled.span`
    margin-left: 5px;
`

export default FingerprintsTable;
//                <tr onClick={row.getToggleSelectedHandler()} key={row.id}